import { Pencil } from 'lucide-react';
import { useState } from 'react';

import { usePagamentosControllerObterPreco } from '@/api/generated/discussões-técnicas-pagamento/discussões-técnicas-pagamento';
import { formatarCentavos } from '@/features/discussoes/rotulos';
import { PrecoDiscussaoFormDialogo } from '@/features/admin/precos-discussao/components/PrecoDiscussaoFormDialogo';
import { EstadoErro } from '@/shared/components/estados';
import { usePermissoes } from '@/shared/permissoes/usePermissoes';
import { Button } from '@/shared/ui/button';
import { Skeleton } from '@/shared/ui/skeleton';

/**
 * Preço da discussão técnica (App × Web) — configurado aqui, consumido pelo
 * wizard de abertura e pela cobrança do Asaas. Append-only no backend: cada
 * envio do diálogo INSERE uma configuração nova, nunca sobrescreve a
 * anterior.
 */
export function PrecosDiscussaoPage() {
  const { pode } = usePermissoes();
  const podeEditar = pode('admin_precos_discussao.editar');

  const [formAberto, setFormAberto] = useState(false);

  const { data, isPending, isError, refetch } = usePagamentosControllerObterPreco();
  // `preco: null` = nenhum preço configurado ainda — o estado inicial da tela,
  // que cai no aviso "Nenhum preço configurado" abaixo, e não em erro.
  const atual = data?.preco ?? undefined;

  return (
    <div className="space-y-6">
      <div className="flex justify-end">
        {podeEditar && (
          <Button onClick={() => setFormAberto(true)}>
            <Pencil className="w-4 h-4" />
            {atual && atual.valorAppCentavos > 0 ? 'Editar preço' : 'Definir preço'}
          </Button>
        )}
      </div>

      <div className="bg-surface-raised rounded-lg border-2 border-brand/20 overflow-hidden">
        <div className="px-6 py-4 bg-gradient-to-r from-brand/5 to-transparent border-b border-brand/10">
          <h3 className="text-sm font-semibold text-brand uppercase tracking-wide">Preço vigente</h3>
        </div>

        {isPending ? (
          <div className="p-6 space-y-3">
            <Skeleton className="h-10 rounded-md" />
            <Skeleton className="h-10 rounded-md" />
          </div>
        ) : isError ? (
          <EstadoErro aoTentarDeNovo={() => void refetch()} />
        ) : atual && atual.valorAppCentavos > 0 ? (
          <div className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div>
              <p className="text-sm text-muted-foreground">Preço vigente no App</p>
              <p className="text-2xl font-semibold text-foreground mt-1">
                {formatarCentavos(atual.valorAppCentavos)}
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Preço vigente no Web</p>
              <p className="text-2xl font-semibold text-foreground mt-1">
                {formatarCentavos(atual.valorWebCentavos)}
              </p>
              <p className="text-xs text-muted-foreground mt-0.5">
                {atual.percentualDescontoWeb}% mais barato que o App
              </p>
            </div>
          </div>
        ) : (
          <div className="p-6 text-sm text-muted-foreground">
            Nenhum preço configurado ainda
            {podeEditar ? ' — defina o primeiro acima.' : '.'}
          </div>
        )}
      </div>

      <PrecoDiscussaoFormDialogo
        aberto={formAberto}
        aoFechar={() => setFormAberto(false)}
        atual={atual}
      />
    </div>
  );
}
